import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getFaqs,
  saveFaq,
  deleteFaq,
} from "../../../core/data/redux/slices/FaqSlice";
import DeleteModal from "../../../core/common/modals/DeleteModal";
import { ReloadOutlined } from "@ant-design/icons";

const ContactFaq = () => {
  const dispatch = useDispatch();
  const { faqs, loading, error } = useSelector((state) => state.faq);
  const [selectedFaqId, setSelectedFaqId] = useState(null);
  const [deleteModalText, setDeleteModalText] = useState("");
  const userProfile = useSelector((state) => state.profile);

  const [refreshing, setRefreshing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(faqs.length === 0);

  const [faqForm, setFaqForm] = useState({
    faqId: "",
    question: "",
    answer: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (faqs.length === 0) {
      dispatch(getFaqs()).then(() => setIsInitialLoad(false));
    } else {
      setIsInitialLoad(false);
    }
  }, [dispatch]);

  const handleSaveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) return;
    await dispatch(saveFaq(faqForm)).unwrap();
    setFaqForm({ faqId: "", question: "", answer: "" });
  };

  const handleEdit = (faq) => {
    setFaqForm({ faqId: faq._id, question: faq.question, answer: faq.answer });
  };

  const handleDelete = async (faqId) => {
    await dispatch(deleteFaq(faqId)).unwrap();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setIsInitialLoad(true);
    await dispatch(getFaqs());
    setIsInitialLoad(false);
    setRefreshing(false);
  };

  const filteredFaqs = faqs?.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card">
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
            <h4 className="fw-semibold align-self-start align-self-md-center mb-md-0 mb-3  text-start">
              Contact FAQs
            </h4>

            <div style={{ display: "flex" }} className="align-self-stretch align-self-md-center justify-content-between align-items-center">
              <div className="icon-form  me-2 mb-sm-0" style={{flex:1}}>
                <span className="form-icon">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search FAQs..."
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                className="btn btn-outline-secondary me-2"
                onClick={handleRefresh}
                disabled={refreshing || loading}
              >
                {refreshing ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <ReloadOutlined className="me-1" />
                )}
                Refresh
              </button>

              {userProfile.role === "companyAdmin" && (
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#faq_modal"
                  onClick={() =>
                    setFaqForm({ faqId: "", question: "", answer: "" })
                  }
                >
                  + Add FAQ
                </button>
              )}
            </div>
          </div>

          <div className="accordion px-5 mb-5" id="faqAccordion">
            {error && <p className="text-danger">{error.message}</p>}
            {isInitialLoad
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="accordion-item mb-2">
                    <div className="contact-skel" style={{ height: 52, borderRadius: 6, width: "100%" }} />
                  </div>
                ))
              : filteredFaqs?.map((faq, index) => (
              <div className="accordion-item mb-2" key={faq._id}>
                <h2 className="accordion-header" id={`heading${index}`}>
                  <button
                    className={`accordion-button ${
                      index !== 0 ? "collapsed" : ""
                    }`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse${index}`}
                    aria-expanded={index === 0 ? "true" : "false"}
                    aria-controls={`collapse${index}`}
                  >
                    {faq.question}
                  </button>
                </h2>
                <div
                  id={`collapse${index}`}
                  className={`accordion-collapse collapse ${
                    index === 0 ? "show" : ""
                  }`}
                  aria-labelledby={`heading${index}`}
                  data-bs-parent="#faqAccordion"
                >
                  <div className="accordion-body">
                    <p>{faq.answer}</p>

                    {userProfile.role === "companyAdmin" && (
                      <div className="d-flex justify-content-end">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          data-bs-toggle="modal"
                          data-bs-target="#faq_modal"
                          onClick={() => handleEdit(faq)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          data-bs-toggle="modal"
                          data-bs-target={`#delete_${deleteModalText}`}
                          onClick={() => {
                            setSelectedFaqId(faq._id);
                            setDeleteModalText("FAQ");
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="modal fade"
          id="faq_modal"
          role="dialog"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <h4 className="mb-3 text-center">
                  {faqForm.faqId ? "Edit FAQ" : "Add FAQ"}
                </h4>

                <div className="mb-3">
                  <label className="col-form-label">
                    Question <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="question"
                    value={faqForm.question}
                    onChange={(e) =>
                      setFaqForm({ ...faqForm, question: e.target.value })
                    }
                    className="form-control"
                    placeholder="Enter question"
                  />
                </div>

                <div className="mb-3">
                  <label className="col-form-label">
                    Answer <span className="text-danger">*</span>
                  </label>
                  <textarea
                    name="answer"
                    value={faqForm.answer}
                    onChange={(e) =>
                      setFaqForm({ ...faqForm, answer: e.target.value })
                    }
                    className="form-control"
                    placeholder="Enter answer"
                    rows={3}
                  />
                </div>

                <div className="text-center">
                  <button
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    data-bs-dismiss="modal"
                    onClick={handleSaveFaq}
                  >
                    {loading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : faqForm.faqId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeleteModal
        text={deleteModalText}
        onDelete={() => handleDelete(selectedFaqId)}
      />
    </div>
  );
};

export default ContactFaq;
